import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  IServiceStatus,
  IServiceTicket,
} from "../../models/serviceManagement.model";
import Loading from "../AssetsComponent/loading";
import { getServiceTicketsByStatus } from "../../store/slices/serviceManagementSlice";
import { RootDispatch } from "../../store";
import { useDispatch } from "react-redux";
import ServiceKanbanCard from "./ServiceKanbanCard";

interface ServiceKanbanColumnProps {
  status: IServiceStatus;
  tickets: IServiceTicket[];
  loading: boolean;
  hasMore: boolean;
  totalCount: number;
  overColumnId: string | null;
  onLoadMore: () => void;
}

const SCROLL_THRESHOLD_PX = 100;

const ServiceKanbanColumn: React.FC<ServiceKanbanColumnProps> = ({
  status,
  tickets,
  loading,
  hasMore,
  totalCount,
  overColumnId,
  onLoadMore,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { setNodeRef } = useDroppable({
    id: status.status,
    data: { status: status.status },
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const [initialLoad, setInitialLoad] = useState(true);

  const isOver = status.status === overColumnId;

  useEffect(() => {
    if (initialLoad) {
      dispatch(getServiceTicketsByStatus({ status: status.status }));
      setInitialLoad(false);
    }
  }, [dispatch, status.status, initialLoad]);

  // Save scroll position to ref (memory only - resets on refresh)
  const saveScrollPosition = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      scrollPositionRef.current = el.scrollTop;
    }
  }, []);

  // Restore scroll position from ref
  const restoreScrollPosition = useCallback(() => {
    const el = scrollRef.current;
    if (el && scrollPositionRef.current > 0) {
      // Only restore if we have a saved position
      setTimeout(() => {
        el.scrollTop = scrollPositionRef.current;
      }, 0);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (loading || !hasMore) return;

    const { scrollTop, clientHeight, scrollHeight } = el;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceFromBottom <= SCROLL_THRESHOLD_PX) {
      // Save current scroll position before loading more
      saveScrollPosition();
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore, saveScrollPosition]);

  // Restore scroll position when tickets update (but not on initial load)
  useEffect(() => {
    if (tickets.length > 0 && !loading && scrollPositionRef.current > 0) {
      restoreScrollPosition();
    }
  }, [tickets.length, loading, restoreScrollPosition]);

  // Reset scroll position when component mounts (page refresh)
  useEffect(() => {
    // Reset scroll position ref on mount (happens on page refresh)
    scrollPositionRef.current = 0;

    // Also reset the actual scroll position to top
    const el = scrollRef.current;
    if (el) {
      setTimeout(() => {
        el.scrollTop = 0;
      }, 0);
    }
  }, []);

  return (
    <div
      ref={setNodeRef}
      className={`min-w-[320px] bg-surface border border-gray-100 rounded-xl shadow-sm p-4 flex flex-col transition-colors duration-150 ${
        isOver ? "bg-blue-50 border-blue-400" : ""
      }`}
      style={{ height: "70vh" }}
    >
      <div className="flex items-center justify-between mb-3 pb-2 border-b">
        <h3 className="text-sm font-semibold text-gray-900 truncate">
          {status.status.capitalizeWords()}
        </h3>
        <div className="text-xs text-gray-500">
          {totalCount ?? tickets.length}
        </div>
      </div>

      <SortableContext
        items={tickets.map((t) => t._id!)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto space-y-3 pr-1"
        >
          {tickets.map((ticket) => (
            <ServiceKanbanCard
              key={ticket._id}
              ticket={ticket}
              status={status.status}
            />
          ))}

          {loading && (
            <div className="flex justify-center py-2">
              <Loading />
            </div>
          )}

          {tickets.length === 0 && !loading && (
            <div className="flex text-xs text-gray-400 mt-4 h-[55vh] text-center justify-center items-end">
              No tickets in this column
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

export default ServiceKanbanColumn;
